import { memo, useCallback, Fragment, useEffect, useMemo, useState } from 'react'
import Divider from '@mui/material/Divider'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../store'
import SubtleShimmer from '../../common/SubtleShimmer'
import AlertsListSkeleton from '../AlertsListSkeleton'
import { UserAlert_Type, UserAlertLog } from '../../../proto/generated/nexus'
import { fetchUserAlertLogs } from '../../../services/apis/alerts'
import AlertLogListItem from '../alertLogListItem/AlertLogListItem'
import { EmptyState, GhostRows } from '../../common/EmptyState'

function AlertLogs() {
    const dispatch = useDispatch()
    const alertLogs = useSelector((state: RootState) => state.alertLogsStore.logs)
    const [loading, setLoading] = useState<boolean>(true)
    const [selectedAlertId, setSelectedAlertId] = useState<string | undefined>()

    useEffect(() => {
        handleAlertLogsFetch().finally(() => {
            setLoading(false)
        })
    }, [])

    const handleAlertLogsFetch = useCallback(async (): Promise<boolean> => {
        setLoading(true)
        let response = false
        try {
            await fetchUserAlertLogs({ dispatch: dispatch })
            response = true
        } catch (error) {
            response = false
        } finally {
            setLoading(false)
        }
        return response
    }, [dispatch])

    const emptyListView = useMemo(() => {
        return <EmptyState preview={<GhostRows variant="alertLogs" />} text="Alerts will appear here when they trigger." />
    }, [])

    const loadingView = useMemo(() => (
        <SubtleShimmer loading fill>
            <AlertsListSkeleton />
        </SubtleShimmer>
    ), [])

    const alertsView = useMemo(() => {
        return (
            <>
                {alertLogs
                    .filter((userAlertLog?: UserAlertLog) => Boolean(userAlertLog))
                    .map((userAlertLog: UserAlertLog) => {
                        if (userAlertLog.alert?.type === UserAlert_Type.SCREENER) return null
                        return (
                            <Fragment key={userAlertLog.id}>
                                <AlertLogListItem log={userAlertLog} alertType="log" />
                                <Divider />
                            </Fragment>
                        )
                    })}
            </>
        )
    }, [alertLogs, selectedAlertId])

    const content = useMemo(() => {
        return loading ? loadingView : alertLogs.length === 0 ? emptyListView : alertsView
    }, [loadingView, alertsView, emptyListView, loading, alertLogs.length === 0])

    const jsxTree = useMemo(() => {
        return content
    }, [content])

    return jsxTree
}

export default memo(AlertLogs)
